import { NoSsr } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { isAuthenticatedState } from '../../../api/appState';
import { CatalogueProduct } from '../../../api/products/catalogue';
import { activeFavoriteProducts, showStockAvailability } from '../../../config';
import { isProductCanceled } from '../../../helpers/isProductCanceled';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailFavoriteToggle from '../../ProductDetailPage/components/ProductDetailFavoriteToggle';
import ProductDetailAlternatives from '../../ProductDetailPage/components/ProductDetailAlternatives';
import CataloguePageListContentItemRowAddToCart from './CataloguePageListContentItemRowAddToCart';
import CataloguePageListContentItemRowImage from './CataloguePageListContentItemRowImage';
import CataloguePageListContentItemRowInlineParameters from './CataloguePageListContentItemRowInlineParameters';
import CataloguePageListContentItemRowName from './CataloguePageListContentItemRowName';
import CataloguePageListContentItemRowPrice from './CataloguePageListContentItemRowPrice';
import CataloguePageListContentItemRowStockInfo from './CataloguePageListContentItemRowStockInfo';
import CataloguePageListContentItemWarning from './CataloguePageListContentItemWarning';

export interface CataloguePageListContentItemRowProps
  extends StandardDivProps<CataloguePageListContentItemRowClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemRowClassKey = 'root' | 'warning';

const themeSettings = { name: 'CataloguePageListContentItemRow' };

const useStyles = makeStyles(
  (theme) => ({
    root: {
      width: '100%',
      position: 'relative',
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1),
      borderBottom: `2px solid ${darken(theme.palette.divider, 0.4)}`,

      // GRID
      display: 'grid',
      gridColumnGap: 0, // theme.spacing(1),
      gridRowGap: 0, // theme.spacing(1),
      justifyItems: 'stretch',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      gridTemplateRows: 'max-content',

      gridTemplateColumns: '96px auto auto',
      gridTemplateAreas: `
        "image name       stockInfo"
        "image packageInfo       stockInfo"
        "image warning    price"
        "image parameters addToCart"
        "image variants   variants"
      `,
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: 'auto',
        justifyItems: 'center',
        gridTemplateAreas: `
          "image"
          "name"
          "packageInfo"
          "warning"
          "stockInfo"
          "price"
          "otherPrices"
          "addToCart"
          "parameters"
          "variants"
        `,
      },

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-columns': '96px auto auto',
      },
    },
    image: {
      gridArea: 'image',
      width: theme.spacing(12),
      maxHeight: theme.spacing(13.5),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),

      // Fixes grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
        '-ms-grid-row-span': 2,
      },
    },
    name: {
      gridArea: 'name',
      borderBottom: 'none',
      // Fixes grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
        // '-ms-grid-column-span': 2,
      },
    },
    parameters: {
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2,
      },
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(2),
      },
    },
    price: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingBottom: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: theme.spacing(1),
      // '& > div': {
      //   display: 'block',
      //   textAlign: 'right',
      //   paddingRight: theme.spacing(1),
      // },

      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 4,
      },
    },
    addToCartArea: {
      gridArea: 'addToCart',
      display: 'flex',
      justifyContent: 'flex-end',
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 5,
        '-ms-grid-row-span': 2,
      },
    },
    stockInfo: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      [theme.breakpoints.down('xs')]: {
        paddingTop: theme.spacing(2),
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 3,
      },
    },
    variants: {
      gridArea: 'variants',
      borderBottom: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('xs')]: {
        borderBottom: 'none',
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2,
      },
    },
    packageInfo: {
      gridArea: 'packageInfo',
      display: 'flex',
      alignItems: 'flex-start',
      paddingLeft: 20,
      justifyContent: 'flex-center',
      [theme.breakpoints.down('xs')]: {
        borderBottom: 'none',
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2,
      },
    },
    favoriteToggleMobile: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1,
      width: 'min-content',
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    addToCart: {},
    warning: {
      gridArea: 'warning',
      paddingLeft: '12px',
    },
    favoriteToggleNotMobile: {
      display: 'none',
      zIndex: 1,
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
    },
  }),
  themeSettings,
);

const CataloguePageListContentItemRow = React.forwardRef<HTMLDivElement, CataloguePageListContentItemRowProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const t = useTranslateWithFallback();
    const classes = useStyles({ classes: pClasses });
    // const customerRegion = useRecoilValue(customerRegionState);
    const isAuthenticated = useRecoilValue(isAuthenticatedState);

    const productIsCanceled = isProductCanceled(product.taxonomies);

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <CataloguePageListContentItemRowImage className={classes.image} product={product} />
        <CataloguePageListContentItemRowName className={classes.name} product={product} />
        <CataloguePageListContentItemRowInlineParameters className={classes.parameters} product={product} />
        {/* {isAuthenticated && (
          <TextFieldPreview
            label={t('cataloguePage/item/package')}
            value={(customerRegion === 'SK' ? product.extra?.packSizeTextSK : product.extra?.packSizeTextCZ) || ''}
            size='small'
            align='left'
            alignLabel
            className={classes.packageInfo}
          />
        )} */}
        {activeFavoriteProducts && isAuthenticated && (
          <NoSsr>
            <ProductDetailFavoriteToggle className={classes.favoriteToggleMobile} productId={product.id} />
          </NoSsr>
        )}
        {productIsCanceled === false ? (
          <>
            <CataloguePageListContentItemWarning className={classes.variants} product={product} type='detail' />
            {showStockAvailability && isAuthenticated && (
              <CataloguePageListContentItemRowStockInfo className={classes.stockInfo} product={product} />
            )}
            {isAuthenticated && <CataloguePageListContentItemRowPrice className={classes.price} product={product} />}
            {isAuthenticated && (
              <div className={classes.addToCartArea}>
                {activeFavoriteProducts && (
                  <NoSsr>
                    <ProductDetailFavoriteToggle className={classes.favoriteToggleNotMobile} productId={product.id} />
                  </NoSsr>
                )}
                <CataloguePageListContentItemRowAddToCart className={classes.addToCart} product={product} />
              </div>
            )}
          </>
        ) : (
          <>
            {activeFavoriteProducts && isAuthenticated && (
              <div className={classes.addToCartArea}>
                <NoSsr>
                  <ProductDetailFavoriteToggle className={classes.favoriteToggleNotMobile} productId={product.id} />
                </NoSsr>
              </div>
            )}
            <ProductDetailAlternatives
              className={classes.warning}
              sectionName={t('productDetailPage/replacements')}
              productAlternatives={product.replacements}
            />
          </>
        )}
      </div>
    );
  },
);

CataloguePageListContentItemRow.displayName = 'CataloguePageListContentItemRow';

export default withThemeProps(themeSettings)(CataloguePageListContentItemRow);
